import React, { useEffect, useState } from 'react'
import { Icon } from 'src/assets/svg/Icon'
import { ReactHookFc, ReactHookForm } from 'src/components/common/ReactHookForm'
import styled, { css } from 'styled-components'
import { Noto_body1_r16, Noto_subheading_r18 } from 'src/styles/fonts/NotoSansKrFonts'
import { ellip, layoutMaxSize } from 'src/styles/mixin'
import useOutsideClickDetector from 'src/hooks/useOutsideClickDetector.hook'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { SearchFormItemsType } from 'src/types/react-hook-form'
import { useToast } from 'src/provider/ToastContextProvider'
import useSearchForm from 'src/hooks/react-hook-form/useSearchForm.hook'
import { RecommendSearchWordListSpace } from 'src/types/api'
import useLocalStorage from 'src/hooks/useLocalStorage.hook'
import { SubmitErrorHandler } from 'react-hook-form/dist/types/form'
import usePageNav from 'src/hooks/usePageNav.hook'

import useDebounce from 'src/hooks/useDebounce.hook'
import { Container } from 'src/components/common/Container'
import { Typography } from 'src/components/common/Typography'
import {
  searchRecommendWordQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import useParamsType from 'src/hooks/useParamsType.hook'
import usePathMatch from 'src/hooks/usePathMatch.hook'
import RenderIf from 'src/components/common/RenderIf'

const SearchWordInput = () => {
  const { searchResultPageNav, navigatePrev } = usePageNav()
  const { search_word } = useParamsType()
  const [recentHistory, setRecentHistory] = useLocalStorage<string[]>('recent_search_history', [])
  const { openToast } = useToast()
  const { methods, registerAttribute } = useSearchForm()
  const { match_search_result } = usePathMatch()
  const { watch, setValue, setFocus, handleSubmit } = methods
  const { debouncedValue: recommendSearchWord } = useDebounce({
    value: watch('word'),
    delay: 200,
  })

  const { mutateAsync: searchMutate, data } = useCustomMutation<
    RecommendSearchWordListSpace.Request,
    RecommendSearchWordListSpace.Response
  >(searchRecommendWordQueryOption())

  const recommendSearchWordList = data?.Body?.RecommendSearch
  const [searchWordListState, setSearchWordListState] = useState(recommendSearchWordList)

  const recommendSearchWordSubmit = (word: string) => {
    setValue('word', word)
    searchFormOnSuccess({ word })
  }

  const searchFormOnSuccess: SubmitHandler<SearchFormItemsType> = (data) => {
    const { word } = data

    const recentHistoryListUpdate = async () => {
      let result: string[]

      if (!recentHistory) {
        setRecentHistory?.([word])
      } else {
        const firstIndex = recentHistory.indexOf(word)
        firstIndex !== -1 && recentHistory.splice(firstIndex, 1)
        recentHistory?.length >= 10 && recentHistory?.pop()
        result = [word]?.concat(recentHistory)
        setRecentHistory?.(result)
      }
    }

    recentHistoryListUpdate().then(() => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
      setSearchWordListState([])
      searchResultPageNav(word, match_search_result)
    })
  }

  const searchFormOnError: SubmitErrorHandler<SearchFormItemsType> = () =>
    openToast({ contents: '검색어를 입력하고 다시 실행해주세요.' })

  const { outside_ref } = useOutsideClickDetector(recommendSearchWordList?.length !== 0, () =>
    setSearchWordListState([]),
  )

  useEffect(() => {
    search_word !== recommendSearchWord &&
      searchMutate({ Word: recommendSearchWord, Length: '15', Offset: '0' }).then(
        ({ Header, Body }) => {
          if (Header.ErrCode === ERRCODE_SUCCESS) {
            setSearchWordListState(Body?.RecommendSearch)
          }
        },
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendSearchWord])

  useEffect(() => {
    if (!!search_word) {
      setValue('word', search_word)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <FormProvider {...methods}>
      <Container $option="flex_row_space_between" $style={S.SearchBarContainer}>
        <Icon.Arrow
          rotate="right"
          color={'#FFF'}
          size={'24'}
          style={{ padding: '7px' }}
          onClickFn={navigatePrev}
        />
        <ReactHookForm onSubmit={handleSubmit(searchFormOnSuccess, searchFormOnError)}>
          <ReactHookFc.Input {...registerAttribute.word} style={S.SearchInputStyle} />
        </ReactHookForm>
        <RenderIf when={!!watch('word')}>
          <Icon.FillRoundCancle
            style={{ padding: '7px' }}
            onClickFn={() => {
              setValue('word', '')
              setFocus('word')
            }}
          />
        </RenderIf>
        <Icon.Search
          style={{ padding: '7px' }}
          onClickFn={handleSubmit(searchFormOnSuccess, searchFormOnError)}
        />
      </Container>
      <Container
        $option="flex_column"
        ref={outside_ref}
        $style={S.RecommendWordListContainer(Boolean(searchWordListState?.length))}
      >
        {searchWordListState?.map((word: string, index: number) => (
          <S.RecommendResultWord key={index} onClick={() => recommendSearchWordSubmit(word)}>
            {word}
          </S.RecommendResultWord>
        ))}
      </Container>
    </FormProvider>
  )
}
export default SearchWordInput

const S = {
  SearchBarContainer: css`
    align-items: center;
    background: var(--black-100);
    height: 66px;
    padding: 14px 10px;
    gap: 10px;
  `,
  SearchInputStyle: css`
    width: 100%;
    display: flex;
    height: 38px;
    ${Noto_subheading_r18};
    margin: 0 5px;
    color: var(--white);

    &::placeholder {
      color: var(--black-50);
    }
  `,
  RecommendWordListContainer: (active: boolean) => css`
    display: ${active ? 'flex' : 'none'};
    position: absolute;
    top: 66px;
    background: var(--white);
    z-index: 2;
    ${layoutMaxSize};
  `,
  RecommendResultWord: styled(Typography)`
    cursor: pointer;
    padding: 12px 40px;
    ${ellip('line1')};
    ${Noto_body1_r16};
    color: var(--black-60);
  `,
}
